import { useEffect, useRef } from "react";

/**
 * Adds `is-visible` class to elements with `.reveal` once they enter viewport.
 * Mount once at the top of a page; it scopes within `document` by default,
 * but accepts an optional ref for narrower scope.
 */
export function useReveal(rootRef = null) {
  const fallbackRef = useRef(null);
  const ref = rootRef || fallbackRef;

  useEffect(() => {
    const root =
      ref && ref.current ? ref.current : document.querySelector("body");
    if (!root) return;

    const targets = root.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window) || targets.length === 0) {
      targets.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    targets.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ref]);

  return ref;
}
